import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import transition from 'styled-transition-group';

import Portal from 'components/common/utilities/portal';
import StackContainer from 'components/common/containers/stack_container';

// Documentation: http://go.glad.ly/style/#/Common%20Components/Toast

const toastPortalId = 'TOAST_PORTAL';
const fadeTimeout = {
  appear: 4000,
  enter: 4000,
  exit: 500,
};
const Toast = props => {
  const node = document && document.getElementById(toastPortalId);
  const [visible, setVisible] = useState(true);
  const setHidden = useCallback(() => setVisible(false), [setVisible]);
  const { onClick } = props;
  const wrappedOnClick = onClick
    ? () => {
        if (onClick) onClick();
        setHidden();
      }
    : null;
  let RenderToast;
  switch (props.type) {
    case 'ERROR':
      RenderToast = ErrorToast;
      break;
    case 'SUCCESS':
      RenderToast = SuccessToast;
      break;
    case 'WARN':
      RenderToast = WarnToast;
      break;
    case 'INFO':
    default:
      RenderToast = InfoToast;
      break;
  }
  return (
    <Portal node={node}>
      <Fade in={visible} timeout={fadeTimeout} appear unmountOnExit onEntered={setHidden}>
        <RenderToast {...props} onClick={wrappedOnClick}>
          {props.children}
        </RenderToast>
      </Fade>
    </Portal>
  );
};

Toast.propTypes = {
  type: PropTypes.oneOf(['ERROR', 'INFO', 'SUCCESS', 'WARN']).isRequired,
  onClick: PropTypes.func,
};

Toast.defaultProps = {
  type: 'INFO',
};

export const ToastPortal = styled.div.attrs({ id: toastPortalId })`
  align-items: center;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-items: center;
  left: 0;
  margin-top: 76px;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 3;
`;

const ToastBase = styled(StackContainer)`
  background-color: ${p => p.theme.colors.white};
  border: 1px solid;
  border-radius: 8px;
  box-shadow: ${p => p.theme.boxShadow.xsmall};
  cursor: ${p => (p.onClick ? 'pointer' : 'default')};
  font-size: ${p => p.theme.fontSize.base};
  font-weight: bold;
  line-height: ${p => p.theme.lineHeight.base};
  min-width: 300px;
  padding: 16px 24px;
  pointer-events: auto;
  text-align: center;
  top: 0px;
`;

export const ErrorToast = styled(ToastBase)`
  border-color: ${p => p.theme.colors.red400};
  color: ${p => p.theme.colors.red400};
`;
export const InfoToast = styled(ToastBase)`
  border-color: ${p => p.theme.colors.purple400};
  color: ${p => p.theme.colors.purple400};
`;
export const SuccessToast = styled(ToastBase)`
  border-color: ${p => p.theme.colors.green400};
  color: ${p => p.theme.colors.green400};
`;
export const WarnToast = styled(ToastBase)`
  border-color: ${p => p.theme.colors.yellow400};
  color: ${p => p.theme.colors.yellow700};
`;
export const Fade = transition.div`
  &:appear {
    opacity: 0.01;
    transform: translateY(-20px);
  }
  &:appear-active {
    opacity: 1;
    transform: translateY(0);
    transition: all 500ms ease;
  }
  &:exit {
    opacity: 1;
  }
  &:exit-active {
    opacity: 0.01;
    transform: translateY(-20px);
    transition: all 500ms ease;
  }
`;

export default Toast;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/common/toast.jsx